import { useCallback } from 'react';

import {
  ConditionsReactivateGenreSimplifieDto,
  GenreSimplifieDto,
  GenreSimplifieDtoFormatsPlaqueEnum,
  GenreSimplifieRequestDto,
  GenreSimplifieRequestDtoFormatsPlaquesEnum,
  ReferentielSearchResultDtoGenreSimplifieDto,
  ReferentielUsesDto,
} from 'lib_api/lib/api/gen/api';

import {
  FetchDataResult,
  TablePagination,
  TableSort,
} from 'components/WrappedComponents/Table/types';
import { useApi } from 'hooks/ApiStoreContext';
import { createEnumMapper } from 'utils/enumConverter';

import {
  FilterDashboardGenreSimplifie,
  GenreSimplifieFormValue,
  GenreSimplifieSort,
  useChangeValidityGenreSimplifieReturn,
} from './types';

export function useSearchGenreSimplifie(
  filter: FilterDashboardGenreSimplifie,
  sort: TableSort<GenreSimplifieSort>,
  pagination: TablePagination,
): [
  () => Promise<ReferentielSearchResultDtoGenreSimplifieDto>,
  (
    result: ReferentielSearchResultDtoGenreSimplifieDto,
  ) => FetchDataResult<GenreSimplifieDto>,
] {
  const controller = useApi().AdminFonctionnelControllerApi;

  return [
    () => {
      return controller.searchGenreSimplifieUsingSpecsUsingGET({
        filters: {
          ...filter,
          ...pagination,
        },
        sorts: {
          libelle: sort?.LIBELLE,
        },
      });
    },
    result => {
      return {
        data: result.results.referenceDtoList,
        total: result.total,
      };
    },
  ];
}

export function buildDto(
  values: GenreSimplifieFormValue,
): GenreSimplifieRequestDto {
  const plaqueEnumMapper = createEnumMapper(
    GenreSimplifieDtoFormatsPlaqueEnum,
    GenreSimplifieRequestDtoFormatsPlaquesEnum,
  );

  return {
    libelle: values.libelle ?? null,
    dicem: !!values.dicem,
    formatsPlaques: values.formatsPlaque?.map(plaqueEnumMapper) ?? null,
  };
}

export function buildDefaultUpdateFormValues(
  genreSimplifie: GenreSimplifieDto,
): GenreSimplifieFormValue {
  return {
    libelle: genreSimplifie.libelle,
    dicem: genreSimplifie.dicem,
    formatsPlaque: genreSimplifie.formatsPlaque,
  };
}

export function useChangeValidityGenreSimplifie(): useChangeValidityGenreSimplifieReturn {
  const controller = useApi().AdminFonctionnelControllerApi;

  const submitDeprecateGenreSimplifie = useCallback(
    (idCorrelation: string): Promise<Response> => {
      return controller.deprecateGenreSimplifieUsingPUT(idCorrelation);
    },
    [controller],
  );

  const submitReactivateGenreSimplifie = useCallback(
    (idCorrelation: string): Promise<Response> => {
      return controller.reactivateGenreSimplifieUsingPUT(idCorrelation);
    },
    [controller],
  );

  const conditionsReactivateGenreSimplifie = useCallback(
    (idCorrelation: string): Promise<ConditionsReactivateGenreSimplifieDto> => {
      return controller.conditionReactivationGenreSimplifieUsingGET(
        idCorrelation,
      );
    },
    [controller],
  );

  const getUsesGenreSimplifie = useCallback(
    (idCorrelation: string): Promise<ReferentielUsesDto> => {
      return controller.searchUsesGenreSimplifieUsingGET(idCorrelation);
    },
    [controller],
  );

  return {
    submitReactivateGenreSimplifie,
    submitDeprecateGenreSimplifie,
    conditionsReactivateGenreSimplifie,
    getUsesGenreSimplifie,
  };
}
